import { useEffect, useRef, useState } from "react";
import { Send, MessageSquare, Building2, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/shared";
import { toast } from "sonner";

export const MessagesPanel = () => {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvs = () => api.get("/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([]));
  useEffect(() => { loadConvs(); }, []);

  const openConv = (c) => {
    setActive(c);
    api.get(`/conversations/${c.id}/messages`).then((r) => setThread(r.data)).catch(() => setThread({ messages: [] }));
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/conversations/${active.id}/messages`, { body: text });
      setThread((t) => ({ ...t, messages: [...t.messages, data] }));
      setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!convs) return <div className="h-64 flex items-center justify-center"><div className="w-7 h-7 border-2 border-[#007F83] border-t-transparent rounded-full animate-spin" /></div>;
  if (convs.length === 0) return <EmptyState title="No conversations yet" subtitle="When a company responds to your job (or you respond to one as a company), a private conversation opens here." />;

  return (
    <div className="grid md:grid-cols-3 gap-4 bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden" data-testid="messages-panel" style={{ minHeight: 480 }}>
      <div className="border-r border-[#E3E8E8] max-h-[560px] overflow-y-auto">
        {convs.map((c) => (
          <button key={c.id} onClick={() => openConv(c)} data-testid={`conv-${c.id}`}
            className={`w-full text-left p-4 border-b border-[#E3E8E8] hover:bg-[#E8F5F1] transition-colors ${active?.id === c.id ? "bg-[#E8F5F1]" : ""}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm text-[#0B1733] truncate">{c.company_name}</span>
              {c.unread > 0 && <span className="text-[10px] bg-[#007F83] text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
            </div>
            <p className="text-xs text-[#4A5568] truncate mt-0.5">Re: {c.job_title}</p>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[#4A5568] p-8"><MessageSquare size={32} className="mb-2 text-[#007F83]" /> Select a conversation</div>
        ) : (
          <>
            <div className="p-4 border-b border-[#E3E8E8]">
              <p className="font-semibold text-[#0B1733]">{active.company_name}</p>
              <p className="text-xs text-[#4A5568]">Re: {active.job_title}</p>
            </div>
            <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[380px]">
              {thread?.messages?.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.mine ? "bg-[#007F83] text-white" : "bg-[#E8F5F1] text-[#0B1733]"}`}>
                    <span className="flex items-center gap-1 text-[10px] opacity-70 mb-0.5">{m.sender_role === "company" ? <Building2 size={10} /> : <User size={10} />} {m.sender_role}</span>
                    {m.body}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-[#E3E8E8] flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." data-testid="message-input" />
              <Button type="submit" data-testid="message-send-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Send size={16} /></Button>
            </form>
            <p className="px-3 pb-2 text-[10px] text-[#4A5568]">Phone and email stay private. Anything you type here is visible to the other party.</p>
          </>
        )}
      </div>
    </div>
  );
};
